
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

// List of Belgian cities
const belgianCities = [
  { city: "Brussels", postalCode: "1000" },
  { city: "Antwerp", postalCode: "2000" },
  { city: "Ghent", postalCode: "9000" },
  { city: "Charleroi", postalCode: "6000" },
  { city: "Liège", postalCode: "4000" },
  { city: "Bruges", postalCode: "8000" },
  { city: "Namur", postalCode: "5000" },
  { city: "Leuven", postalCode: "3000" },
  { city: "Mons", postalCode: "7000" },
  { city: "Aalst", postalCode: "9300" },
  { city: "Mechelen", postalCode: "2800" },
  { city: "Hasselt", postalCode: "3500" },
  { city: "Kortrijk", postalCode: "8500" },
  { city: "Ostend", postalCode: "8400" },
  { city: "Tournai", postalCode: "7500" }
];

// List of Belgian company names (fictional)
const belgianCompanyNames = [
  "BelgoCorp",
  "Flandertech",
  "Wallonia Solutions",
  "Brussels Digital",
  "Antwerp Innovations",
  "Ghent Systems",
  "Liège Engineering",
  "Brabant Software",
  "Namur Networks",
  "Flanders Dynamics",
  "Belgian Automation",
  "Ardennes Technologies",
  "Meuse Valley Tech",
  "Scheldt Industries",
  "Bruges Analytics",
  "Leuven Biotech",
  "Charleroi Data",
  "Flemish Innovations",
  "Walloon Enterprises",
  "Brussels Consulting"
];

// List of Belgian street names
const belgianStreets = [
  "Rue de la Loi",
  "Avenue Louise",
  "Boulevard Anspach",
  "Rue Royale",
  "Boulevard du Régent",
  "Avenue de Tervuren",
  "Chaussée de Gand",
  "Rue du Midi",
  "Meir",
  "Groenplaats",
  "Korenlei",
  "Graslei",
  "Boulevard de la Sauvenière",
  "Rue Saint-Lambert",
  "Steenstraat",
  "Heverlee",
  "Place Saint-Aubain",
  "Grand-Place",
  "Wetstraat",
  "Grote Markt"
];

// Belgian assignees (fictional names)
const belgianAssignees = [
  "Jean Dupont",
  "Sophie Vandenberghe",
  "Thomas Dubois",
  "Lotte Peeters",
  "Marc Janssens",
  "Charlotte De Vos",
  "Kevin Thibaut",
  "Emma Martens",
  "Nicolas Lambert",
  "Sofie Maes"
];

// Company notes
const companyNotes = [
  "Expanding tech department in Q3",
  "Recently relocated to larger office space",
  "Looking for UX/UI specialists",
  "Interested in AI solutions",
  "Planning to open new branch in Wallonia",
  "Growing rapidly, multiple open positions",
  "Tech-focused company with international clients",
  "Recently merged with smaller startup",
  "Currently restructuring leadership team",
  "Specializes in financial technology",
  "Working on government contracts",
  "Looking for backend developers",
  "Remote-friendly work culture",
  "New projects starting in Q2",
  "Hybrid work model implemented recently"
];

// Generate a random integer between min and max (inclusive)
const getRandomInt = (min: number, max: number) => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

// Get a random item from an array
const getRandomItem = <T,>(array: T[]): T => {
  return array[getRandomInt(0, array.length - 1)];
};

// Generate a random Belgian company
const generateRandomBelgianCompany = () => {
  const cityInfo = getRandomItem(belgianCities);
  const streetName = getRandomItem(belgianStreets);
  const streetNumber = getRandomInt(1, 250);
  
  return {
    name: getRandomItem(belgianCompanyNames) + " " + getRandomItem(["GmbH", "NV", "SA", "BV", "SPRL"]),
    street: `${streetName} ${streetNumber}`,
    postal_code: cityInfo.postalCode,
    city: cityInfo.city,
    country: "Belgium",
    assignee: Math.random() > 0.5 ? getRandomItem(belgianAssignees) : null,
    notes: Math.random() > 0.3 ? getRandomItem(companyNotes) : null,
    is_dummy_data: true
  };
};

interface DummyCompany {
  name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  assignee: string | null;
  notes: string | null;
  is_dummy_data: boolean;
  organization_uuid: string; // Now required, not optional
}

// Generate and insert a specified number of dummy Belgian companies
export const generateDummyBelgianCompanies = async (count = 10) => {
  try {
    // Get the user's organization UUID
    const { data: userOrgData } = await supabase.auth.getSession();
    const userUuid = userOrgData.session?.user?.id;
    
    let organizationUuid = null;
    
    if (userUuid) {
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', userUuid)
        .single();
        
      organizationUuid = userOrg?.organization_uuid;
    }
    
    if (!organizationUuid) {
      throw new Error("No organization UUID found for the current user");
    }
    
    const dummyCompanies: DummyCompany[] = [];
    
    for (let i = 0; i < count; i++) {
      const company = generateRandomBelgianCompany();
      dummyCompanies.push({
        ...company,
        organization_uuid: organizationUuid
      });
    }
    
    // Insert the dummy companies into the database
    const { data, error } = await supabase
      .from('companies')
      .insert(dummyCompanies)
      .select();
    
    if (error) {
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error("Error generating dummy companies:", error);
    toast({
      title: "Error",
      description: "Failed to generate dummy companies",
      variant: "destructive",
    });
    throw error;
  }
};

// Function to delete all dummy companies
export const deleteDummyCompanies = async () => {
  try {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('is_dummy_data', true);
    
    if (error) {
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error("Error deleting dummy companies:", error);
    toast({
      title: "Error",
      description: "Failed to delete dummy companies",
      variant: "destructive",
    });
    throw error;
  }
};
